/**
 * CgvDeposees.jsx — l'espace ou l'on depose ses conditions generales.
 *
 * **Un PDF, et il sert aux deux documents** : il est colle derriere la
 * convention et derriere le devis. Tant que rien n'est depose, la convention
 * imprime les clauses ecrites dans le code et le devis n'annonce aucune
 * annexe — un document qui annonce ce qu'il ne porte pas est exactement ce
 * qu'on refuse.
 *
 * **Le fichier remplace le texte, il ne s'y ajoute pas** : deux versions des
 * memes conditions dans un meme document seraient pires que pas de conditions.
 *
 * Le depot se fait tout de suite — il n'attend pas « Enregistrer » : ce n'est
 * pas un champ du formulaire, c'est un fichier, et le serveur repond s'il
 * n'est pas lisible.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FileText, Trash2, Upload } from 'lucide-react';
import api from '../../api/client';
import { toast } from '../../hooks/use-toast';

const poids = (octets) => {
  const n = Number(octets) || 0;
  if (n < 1024) return `${n} o`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} Ko`;
  return `${(n / (1024 * 1024)).toFixed(1)} Mo`;
};

const leJour = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('fr-FR');
  } catch {
    return '';
  }
};

export default function CgvDeposees({ joindreAuDevis, onJoindreAuDevis }) {
  const [fichier, setFichier] = useState(null);
  const [anciennes, setAnciennes] = useState([]);
  const [occupe, setOccupe] = useState(false);
  const champ = useRef(null);

  const relire = useCallback(() => {
    api.get('/cgv/fichier')
      .then((r) => setFichier(r.data && r.data.nom ? r.data : null))
      .catch(() => setFichier(null));
    // **L'historique survit au retrait** : ce qui est parti chez un client
    // reste produisible, meme sans fichier courant.
    api.get('/cgv/versions')
      .then((r) => setAnciennes(Array.isArray(r.data) ? r.data : []))
      .catch(() => setAnciennes([]));
  }, []);

  useEffect(() => { relire(); }, [relire]);

  const deposer = async (f) => {
    if (!f) return;
    setOccupe(true);
    try {
      const corps = new FormData();
      corps.append('file', f);
      const r = await api.post('/cgv/fichier', corps,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      toast({ title: 'Conditions générales déposées',
              description: `${r.data.nom} · ${r.data.pages} page(s)`
                + (r.data.depose_le ? ` · version du ${leJour(r.data.depose_le)}` : '') });
      relire();
    } catch (e) {
      // Le serveur dit pourquoi : fichier vide, trop lourd, ou pas un PDF
      // lisible. On rend sa phrase plutot qu'un « echec » qui n'apprend rien.
      toast({
        title: 'Dépôt refusé',
        description: e?.response?.data?.detail || 'Le fichier n’a pas pu être déposé.',
        variant: 'destructive',
      });
    } finally {
      setOccupe(false);
      if (champ.current) champ.current.value = '';
    }
  };

  const retirer = async () => {
    setOccupe(true);
    try {
      await api.delete('/cgv/fichier');
      toast({ title: 'Fichier retiré',
              description: 'Les documents reprennent les clauses écrites.' });
      relire();
    } finally {
      setOccupe(false);
    }
  };

  const ouvrirPdf = async (url, quoi) => {
    try {
      const r = await api.get(url, { responseType: 'blob' });
      const lien = URL.createObjectURL(r.data);
      window.open(lien, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(lien), 60000);
    } catch {
      toast({ title: `Relecture impossible${quoi ? ` (${quoi})` : ''}`,
              variant: 'destructive' });
    }
  };

  // Le fichier courant s'ouvre par le meme chemin qu'une version : deux
  // facons d'ouvrir un PDF finiraient par ne pas se comporter pareil.
  const relirePdf = () => ouvrirPdf('/cgv/fichier/pdf');

  return (
    <section className="mb-6" data-testid="cgv-deposees">
      <label className="block text-[12.5px] font-semibold text-[#4A4B63] mb-1.5">
        Conditions générales de vente (PDF)
      </label>

      {fichier ? (
        <div className="flex items-center gap-3 p-3 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6]">
          <FileText className="w-5 h-5 text-[#4F46E5] shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="text-[13px] font-semibold text-[#1A1B2E] truncate">
              {fichier.nom}
            </div>
            <div className="text-[11px] text-[#8E8FA5]">
              {fichier.pages} page(s) · {poids(fichier.octets)}
              {fichier.depose_le ? ` · version du ${leJour(fichier.depose_le)}` : ''}
            </div>
          </div>
          <button type="button" onClick={relirePdf}
                  className="text-[12px] font-semibold text-[#4F46E5] hover:underline shrink-0">
            Relire
          </button>
          <button type="button" onClick={() => champ.current?.click()} disabled={occupe}
                  className="text-[12px] font-semibold text-[#4A4B63] hover:underline shrink-0">
            Remplacer
          </button>
          <button type="button" onClick={retirer} disabled={occupe}
                  title="Retirer le fichier"
                  className="h-8 w-8 rounded-lg text-[#B91C1C] hover:bg-[#FEF2F2] flex items-center justify-center shrink-0">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => champ.current?.click()}
          disabled={occupe}
          className="w-full p-4 rounded-xl border border-dashed border-[#D6D7E3] bg-[#FAFAFE] text-[12.5px] text-[#6B6C85] hover:border-[#4F46E5] hover:bg-white flex items-center justify-center gap-2"
          data-testid="cgv-deposer"
        >
          <Upload className="w-4 h-4" />
          {occupe ? 'Dépôt en cours…' : 'Déposer vos CGV au format PDF'}
        </button>
      )}

      <input
        ref={champ} type="file" accept="application/pdf,.pdf" className="hidden"
        onChange={(e) => deposer(e.target.files && e.target.files[0])}
      />

      {anciennes.length > 0 && (
        <div className="mt-2.5 rounded-xl border border-[#EFF0F6] overflow-hidden"
             data-testid="cgv-versions">
          <div className="px-3 py-2 bg-[#FAFAFE] text-[11px] font-semibold text-[#4A4B63]">
            Versions déposées &mdash; ce qu&rsquo;on ressort si un client conteste une clause
          </div>
          {anciennes.map((v) => (
            <div key={v.id}
                 className="px-3 py-2 flex items-center gap-3 border-t border-[#EFF0F6] text-[11.5px]">
              <span className="text-[#1A1B2E] font-semibold shrink-0">
                {leJour(v.depose_le) || '—'}
              </span>
              <span className="text-[#8E8FA5] min-w-0 flex-1 truncate">
                {v.nom} · {v.pages} page(s) · {poids(v.octets)}
                {v.empreinte ? ` · ${String(v.empreinte).slice(0, 12)}` : ''}
              </span>
              <button type="button"
                      onClick={() => ouvrirPdf(`/cgv/versions/${v.id}/pdf`, v.nom)}
                      className="text-[11.5px] font-semibold text-[#4F46E5] hover:underline shrink-0">
                Relire
              </button>
            </div>
          ))}
        </div>
      )}

      <p className="text-[11px] text-[#8E8FA5] mt-1.5">
        Déposées, elles sont jointes à la convention <strong>et</strong> au devis, et
        elles remplacent les clauses écrites dans le modèle. Sans fichier, la
        convention imprime ces clauses et le devis n’annonce aucune annexe.
        <br /><strong>La date du dépôt est la date de version</strong>&nbsp;: le
        devis annonce «&nbsp;CGV du&nbsp;…&nbsp;». Rien à saisir — pour changer
        de version, on dépose le nouveau fichier, et <strong>l&rsquo;ancien est
        gardé</strong> avec son empreinte.
      </p>

      <label className="flex items-center gap-2 text-[13px] text-[#4A4B63] cursor-pointer mt-2.5">
        <input
          type="checkbox"
          checked={joindreAuDevis !== false}
          onChange={(e) => onJoindreAuDevis(e.target.checked)}
          data-testid="cgv-joindre-devis"
          className="w-4 h-4 rounded text-[#4F46E5]"
        />
        <span>Joindre les CGV au devis (la convention les porte toujours)</span>
      </label>
    </section>
  );
}
